import { TRACKING } from 'common/constants/constants';
import { ICON_SIZES } from 'common/constants/sizes';
import * as TrackingService from 'common/services/tracking.service';
import InfoBox from 'components/InfoBox';
import Icon from 'components/base/Icon';
import logo from 'images/logos/linearb-logo-inline.svg';
import Modal from 'modals/Modal';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ConfirmAccountCreation } from './ConfirmAccountCreation';
import {
  StyledButtonWrapper,
  StyledErrorMessage,
  StyledHeader,
  StyledInfo,
  StyledLogo,
  StyledSuccessMessage,
  StyledWrapper
} from './FamiliarOrg.style';
import { GitProviderSelectContext } from './GitProviderSelect.context';
import { LBButton } from '@linearb/buzz';

type InviteState = 'pending' | 'sent' | 'error';

export function FamiliarOrg() {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [inviteState, setInviteState] = useState<InviteState>('pending');
  const { onAskForInvite, familiarOrgName, email } = useContext(
    GitProviderSelectContext
  );

  useEffect(() => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.ONBOARDING,
      page_tab: TRACKING.PAGE_TABS.YOU_LOOK_FAMILIAR,
      click_type: TRACKING.CLICK.STRAY_ORG_SHOW_WARNING
    });
  }, []);

  const handleCreate = useCallback(() => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.ONBOARDING,
      page_tab: TRACKING.PAGE_TABS.YOU_LOOK_FAMILIAR,
      click_type: TRACKING.CLICK.STRAY_ORG_CREATE_NEW_ORG
    });

    setIsConfirmOpen(true);
  }, []);

  const handleConfirmClose = useCallback(() => setIsConfirmOpen(false), []);

  const handleSendInvite = useCallback(async () => {
    try {
      await onAskForInvite();
      setInviteState('sent');
    } catch (err) {
      setInviteState('error');
    } finally {
      handleConfirmClose();
    }
  }, []);

  return (
    <StyledWrapper>
      <StyledHeader variant='h4'>
        looks like your company already created a
        <StyledLogo src={logo} alt='LinearB' /> account
      </StyledHeader>

      <StyledInfo>
        <p>
          Your email <strong>{email}</strong> appears to be associated with{' '}
          <strong>{familiarOrgName}</strong>.{' '}
        </p>
        <p>
          You can request an invitation from the{' '}
          <strong>{familiarOrgName}</strong> admin or create a new organization
          in LinearB.
        </p>
      </StyledInfo>

      <InfoBox
        iconAlign='center'
        padding='x-small'
        mt='none'
        borderColor='orangePeel'
        backgroundColor='orangePeel'
        borderAlpha={0.3}
        backgroundAlpha={0.1}
        iconColor='orangePeel'
        mb={undefined}
        width={undefined}
      >
        <p>
          Please note that an email can only be associated with a single
          organization. Creating a new organization will mean that you cannot
          use this email with the existing <strong>{familiarOrgName}</strong>{' '}
          organization.
        </p>
      </InfoBox>

      <StyledButtonWrapper>
        {inviteState === 'pending' && (
          <>
            <div>
              <LBButton onClick={handleSendInvite}>
                ASK ADMIN FOR INVITE
              </LBButton>
            </div>

            <LBButton variant='link' onClick={handleCreate}>
              Create new organization
            </LBButton>
          </>
        )}

        {inviteState === 'sent' && (
          <>
            <StyledSuccessMessage>
              <Icon
                name='send'
                size={ICON_SIZES['x-small']}
                color={'malachite'}
              />
              invite request sent!
            </StyledSuccessMessage>
            <p>Be sure to check your email for LinearB access!</p>
          </>
        )}

        {inviteState === 'error' && (
          <>
            <StyledErrorMessage>invite request failed</StyledErrorMessage>
            <p>Please Try Again</p>
            <div>
              <LBButton onClick={handleSendInvite}>
                ASK ADMIN FOR INVITE
              </LBButton>
            </div>
          </>
        )}
      </StyledButtonWrapper>

      <Modal
        width={'42rem'}
        height={'20rem'}
        isOpen={isConfirmOpen}
        onClose={handleConfirmClose}
        content={() => (
          <ConfirmAccountCreation onSendInvite={handleSendInvite} />
        )}
      />
    </StyledWrapper>
  );
}
